import React, { useEffect, useRef } from "react";
import { AtomicTextField } from "@adsk/acc-issues-common/components/primitive/AtomicTextField";
import { useFieldTestId } from "../../../utils/components/IssueFieldPresentationContext";
import { isAttributeEditable } from "../../../utils/data/entityPermissions";
import { MAX_ISSUE_TITLE_LENGTH } from "../../../utils/validation/entitySchemas";
import { useTextValidation } from "../../../utils/validation/validationUtils";
import { Issue } from "@adsk/acc-issues-types";
import { useTranslations } from "@adsk/acc-issues-translations";
import { IssueChangeFn } from "./typesForFields";

// This Field is similar to the one in IssuesTab/TemplatesConfig/IssueTemplateDetailPanel/detail-panel-fields/...
// In case any necessary change is needed, it would be nice check on the other file as well
export const TitleField = ({
  T,
  handleChange,
  issue,
  viewOnly = false,
  shouldSelectInputContent = false,
}: {
  T: ReturnType<typeof useTranslations>["T"];
  handleChange: IssueChangeFn;
  issue: Issue;
  viewOnly?: boolean;
  shouldSelectInputContent?: boolean;
}) => {
  const testId = useFieldTestId("title-field");

  const validationSchema = useTextValidation({
    maxLength: MAX_ISSUE_TITLE_LENGTH,
    isRequired: true,
  });
  const isDisabled = viewOnly || !isAttributeEditable(issue, "title");

  const alreadySelected = useRef<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (shouldSelectInputContent && !alreadySelected.current) {
      inputRef?.current?.select();
      alreadySelected.current = true;
    }
  }, [shouldSelectInputContent, inputRef]);

  return (
    <div data-testid={testId}>
      <AtomicTextField
        fieldKey="title"
        label={T.translate("issueAttributes.title")}
        value={issue.title || ""}
        onChange={newValue => {
          handleChange({
            title: newValue,
          });
        }}
        showDisabledReason={!viewOnly}
        validator={text => {
          try {
            validationSchema.validateSync(text);
          } catch (error: any) {
            return error.message;
          }
        }}
        disabled={isDisabled}
        initialEditMode={shouldSelectInputContent && !alreadySelected.current}
        inputRef={inputRef}
      />
    </div>
  );
};
